import {
  INITIAL_DESKTOP_COLUMNS,
  INITIAL_DESKTOP_COLUMNS_WIDTH,
  INITIAL_DESKTOP_ROWS_SETTINGS,
} from '@core/constants';
import {
  ColumnsWidths,
  OutkeepSettings,
  RequestTableColumn,
  RowsSettings,
} from '@core/types';

type NormalizeOutkeepTableSettingsOptions = {
  // Управляет подстановкой дефолтного набора колонок, если у пользователя
  // нет сохраненного списка видимых колонок.
  // `true` — подставляем `INITIAL_DESKTOP_COLUMNS`.
  // `false` — не навязываем колонки и нормализуем только ширины/порядок/строки.
  applyDefaultColumns?: boolean;
};

const VALID_COLUMNS = new Set<RequestTableColumn>(INITIAL_DESKTOP_COLUMNS);

const DEFAULT_ROWS_SETTINGS: RowsSettings =
  INITIAL_DESKTOP_ROWS_SETTINGS.reduce(
    (acc, { key, value }) => ({
      ...acc,
      [key]: value,
    }),
    {} as RowsSettings,
  );

function isValidColumn(
  column: RequestTableColumn | string | undefined,
): column is RequestTableColumn {
  return !!column && VALID_COLUMNS.has(column as RequestTableColumn);
}

function normalizeTableColumns(
  tableColumns: OutkeepSettings['tableColumns'],
  applyDefaultColumns: boolean,
): RequestTableColumn[] | undefined {
  // Если пользователь явно сохранил набор видимых колонок, сохраняем этот выбор,
  // но отфильтровываем колонки, которых больше нет в актуальной схеме.
  if (Array.isArray(tableColumns)) {
    return tableColumns.filter(isValidColumn);
  }

  // Если пользователь никогда не сохранял свой набор колонок,
  // подставляем текущую дефолтную схему таблицы.
  if (applyDefaultColumns) {
    return [...INITIAL_DESKTOP_COLUMNS];
  }

  return undefined;
}

function normalizeTableColumnsWidths(
  tableColumnsWidths: OutkeepSettings['tableColumnsWidths'],
): ColumnsWidths {
  // Старые сохраненные настройки могут не содержать ширины для новых колонок,
  // поэтому ширины всегда домешиваются с дефолтными значениями.
  if (!tableColumnsWidths) {
    return { ...INITIAL_DESKTOP_COLUMNS_WIDTH };
  }

  const validSavedWidths = Object.fromEntries(
    Object.entries(tableColumnsWidths).filter(([column]) =>
      isValidColumn(column),
    ),
  ) as ColumnsWidths;

  return {
    ...INITIAL_DESKTOP_COLUMNS_WIDTH,
    ...validSavedWidths,
  };
}

function normalizeRowsSettings(
  rowsSettings: OutkeepSettings['rowsSettings'],
): RowsSettings {
  // Настройки строк живут отдельно от колонок и тоже должны автоматически
  // получать новые дефолтные значения при расширении схемы.
  return {
    ...DEFAULT_ROWS_SETTINGS,
    ...(rowsSettings ?? {}),
  };
}

function normalizeTableColumnsOrder(
  tableColumns: RequestTableColumn[] | undefined,
): [number, number][] | undefined {
  if (!tableColumns) {
    return undefined;
  }

  // Видимые колонки сохраняют свой порядок, а скрытые дописываются в конец.
  // Это позволяет новым колонкам получить предсказуемую позицию, но не
  // делает их автоматически видимыми для пользователей с кастомным набором.
  const hiddenColumns = INITIAL_DESKTOP_COLUMNS.filter(
    column => !tableColumns.includes(column),
  );
  const orderedColumns = [...tableColumns, ...hiddenColumns];

  return orderedColumns.map((column, index) => [
    INITIAL_DESKTOP_COLUMNS.indexOf(column),
    index,
  ]);
}

// Нормализует сохраненные настройки таблицы под текущую схему колонок.
// Нужен для миграции старых пользовательских настроек после добавления новых колонок:
// дополняет ширины дефолтами, очищает невалидные колонки, восстанавливает порядок
// и домешивает недостающие настройки строк без ручного сброса профиля.
export function normalizeOutkeepTableSettings(
  settings: OutkeepSettings | null | undefined,
  options: NormalizeOutkeepTableSettingsOptions = {},
): OutkeepSettings {
  const { applyDefaultColumns = true } = options;
  const normalizedTableColumns = normalizeTableColumns(
    settings?.tableColumns,
    applyDefaultColumns,
  );

  return {
    ...settings,
    ...(normalizedTableColumns ? { tableColumns: normalizedTableColumns } : {}),
    tableColumnsWidths: normalizeTableColumnsWidths(
      settings?.tableColumnsWidths,
    ),
    rowsSettings: normalizeRowsSettings(settings?.rowsSettings),
    ...(normalizedTableColumns
      ? {
          tableColumnsOrder: normalizeTableColumnsOrder(normalizedTableColumns),
        }
      : {}),
  };
}
